import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { 
  Search,
  Plus,
  Loader2,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Users,
  Droplets,
  TrendingUp,
  Clock,
  Percent,
  Coins,
  Radar,
  Sparkles,
  RefreshCw,
  Eye
} from 'lucide-react';

import { getApiUrl, getAuthenticatedAxios } from '../../utils/api';

const API_URL = getApiUrl();

const BlumTokenScanner = ({ onTokenAdded, tonPrice }) => {
  const [activeTab, setActiveTab] = useState('manual');
  const [tokenAddress, setTokenAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState(null);
  
  // Discovery state
  const [scanning, setScanning] = useState(false);
  const [discoveredTokens, setDiscoveredTokens] = useState([]);
  const [loadingDiscovered, setLoadingDiscovered] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  
  // Manual add state
  const [manualAddress, setManualAddress] = useState('');
  const [addingManual, setAddingManual] = useState(false);
  
  // Auto discovery state
  const [autoStatus, setAutoStatus] = useState(null);
  const [loadingAutoStatus, setLoadingAutoStatus] = useState(false);

  // Load discovered tokens and auto status on mount
  useEffect(() => {
    loadDiscoveredTokens();
    loadAutoStatus();
    
    // Refresh status every 30 seconds
    const interval = setInterval(loadAutoStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadAutoStatus = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/blum/discover/auto/status`);
      if (response.data.success) {
        setAutoStatus(response.data);
      }
    } catch (err) {
      console.error('Error loading auto status:', err);
    }
  };

  const loadDiscoveredTokens = async () => {
    setLoadingDiscovered(true);
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/blum/discover/tokens`, {
        params: { include_watched: false, limit: 50 }
      });
      
      if (response.data.success) {
        setDiscoveredTokens(response.data.tokens || []);
      }
    } catch (err) {
      console.error('Error loading discovered tokens:', err);
    } finally {
      setLoadingDiscovered(false);
    }
  };

  const scanForTokens = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/blum/discover/scan`, null, {
        params: { max_pages: 3, limit: 100, verify_bcl: false }
      });
      
      if (response.data.success) {
        setScanResult(response.data);
        toast.success(`Найдено ${response.data.total_found} BLUM токенов, сохранено ${response.data.new_saved} новых`);
        await loadDiscoveredTokens();
      }
    } catch (err) {
      toast.error('Ошибка сканирования');
      console.error(err);
    } finally {
      setScanning(false);
    }
  };

  const addTokenManually = async () => {
    if (!manualAddress.trim()) {
      toast.error('Введите адрес токена');
      return;
    }
    
    setAddingManual(true);
    try {
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/blum/discover/add`, null, {
        params: { address: manualAddress.trim() }
      });
      
      if (response.data.success) {
        toast.success(`Токен ${response.data.token.symbol} добавлен!`);
        setManualAddress('');
        await loadDiscoveredTokens();
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Ошибка добавления';
      toast.error(msg);
      console.error(err);
    } finally {
      setAddingManual(false);
    }
  };

  const scanToken = async () => {
    if (!tokenAddress.trim()) {
      toast.error('Введите адрес токена');
      return;
    }

    setLoading(true);
    setError(null);
    setTokenInfo(null);

    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/blum/token/${tokenAddress.trim()}`);
      
      if (response.data.success) {
        setTokenInfo(response.data);
        toast.success('Токен найден');
      } else {
        setError(response.data.error || 'Токен не найден');
        toast.error(response.data.error || 'Токен не найден');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Ошибка сканирования';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const addToWatchlist = async (token = null) => {
    const tokenData = token || tokenInfo;
    if (!tokenData) return;

    setAdding(true);
    try {
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/blum/tokens/add`, {
        token_address: tokenData.token_address || tokenData.master_address,
        name: tokenData.name,
        symbol: tokenData.symbol
      });
      
      if (response.data.success) {
        toast.success(`${tokenData.symbol || 'Token'} добавлен в watchlist`);
        if (onTokenAdded) {
          onTokenAdded(response.data.token);
        }
        // Remove from discovered list
        setDiscoveredTokens(prev => 
          prev.filter(t => t.master_address !== (tokenData.token_address || tokenData.master_address))
        );
      } else {
        toast.error(response.data.error || 'Ошибка добавления');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка добавления');
    } finally {
      setAdding(false);
    }
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '—';
    if (num >= 1e9) return (num / 1e9).toFixed(decimals) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(decimals) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(decimals) + 'K';
    return num.toFixed(decimals);
  };

  const formatPrice = (price) => {
    if (!price) return '$0';
    if (price < 0.00001) return `$${price.toExponential(2)}`;
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white/10 border border-white/20 p-1">
          <TabsTrigger 
            value="manual" 
            className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-purple-200"
          >
            <Search className="h-4 w-4 mr-2" />
            Ручной поиск
          </TabsTrigger>
          <TabsTrigger 
            value="auto" 
            className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-purple-200"
          >
            <Radar className="h-4 w-4 mr-2" />
            Авто-обнаружение
            {discoveredTokens.length > 0 && (
              <Badge className="ml-2 bg-green-500 text-white text-xs">
                {discoveredTokens.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Manual Search Tab */}
        <TabsContent value="manual" className="mt-4">
          <Card className="bg-white/10 backdrop-blur-sm border-white/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Search className="h-5 w-5" />
                Поиск по адресу
              </CardTitle>
              <CardDescription className="text-purple-200">
                Введите адрес BCL токена для получения информации
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Input
                  placeholder="EQB... или UQA... (адрес токена)"
                  value={tokenAddress}
                  onChange={(e) => setTokenAddress(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && scanToken()}
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-purple-300"
                />
                <Button 
                  onClick={scanToken}
                  disabled={loading}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  <span className="ml-2">Scan</span>
                </Button>
              </div>
              <p className="text-xs text-purple-300 mt-2">
                Введите адрес токена BLUM (EQ... или UQ...)
              </p>
              <p className="text-xs text-purple-400 mt-1">
                💡 Совет: Для очень новых токенов (менее 2ч) используйте "Добавить вручную" в авто-обнаружении
              </p>
            </CardContent>
          </Card>

          {/* Error State */}
          {error && (
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-4 flex items-center gap-3">
                <XCircle className="h-5 w-5 text-red-400" />
                <span className="text-red-200">{error}</span>
              </CardContent>
            </Card>
          )}

          {/* Token Info Card */}
          {tokenInfo && (
            <TokenInfoCard 
              tokenInfo={tokenInfo}
              tonPrice={tonPrice}
              onAdd={() => addToWatchlist()}
              adding={adding}
            />
          )}
        </TabsContent>

        {/* Auto Discovery Tab */}
        <TabsContent value="auto" className="mt-4 space-y-4">
          <Card className="bg-white/10 backdrop-blur-sm border-white/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Radar className="h-5 w-5" />
                    BCL токены BLUM
                    {autoStatus?.running && (
                      <span className="flex items-center gap-1 text-green-400 text-sm font-normal">
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                        Live
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription className="text-purple-200">
                    {autoStatus?.running 
                      ? `${autoStatus.known_tokens || 0} токенов в базе`
                      : 'Список BCL токенов'}
                  </CardDescription>
                </div>
                <Button
                  onClick={scanForTokens}
                  disabled={scanning}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                >
                  {scanning ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Сканирование...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Сканировать
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Manual add section */}
              <div className="bg-white/5 rounded-lg p-4 border border-white/10 mb-4">
                <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Добавить токен вручную
                </h4>
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-3">
                  <p className="text-yellow-200 text-xs">
                    ⚡ Новые токены BLUM (менее 1 часа) не индексируются автоматически.
                    <br />
                    📋 Скопируйте адрес токена из BLUM app → нажмите на токен → Copy Address
                  </p>
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="EQAwOyu6KCskhsON7bf3VxHAuWhlkGwCI9..."
                    value={manualAddress}
                    onChange={(e) => setManualAddress(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addTokenManually()}
                    className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-purple-300/50 text-sm"
                  />
                  <Button
                    onClick={addTokenManually}
                    disabled={addingManual}
                    size="sm"
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    {addingManual ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {scanResult && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10 mb-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                    <div>
                      <div className="text-purple-300 text-xs">Найдено BLUM</div>
                      <div className="text-white text-xl font-bold">{scanResult.total_found}</div>
                    </div>
                    <div>
                      <div className="text-purple-300 text-xs">Новых</div>
                      <div className="text-green-400 text-xl font-bold">{scanResult.new_saved}</div>
                    </div>
                    <div>
                      <div className="text-purple-300 text-xs">Обновлено</div>
                      <div className="text-blue-400 text-xl font-bold">{scanResult.updated}</div>
                    </div>
                    <div>
                      <div className="text-purple-300 text-xs">Источник</div>
                      <div className="text-white text-sm">{scanResult.source || 'TonAPI'}</div>
                    </div>
                    <div>
                      <div className="text-purple-300 text-xs">Время</div>
                      <div className="text-white text-sm">{formatDate(scanResult.scanned_at)}</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-medium">
                  Обнаруженные токены ({discoveredTokens.length})
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadDiscoveredTokens}
                  disabled={loadingDiscovered}
                  className="bg-white/5 border-white/20 text-purple-200 hover:bg-white/10"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${loadingDiscovered ? 'animate-spin' : ''}`} />
                  Обновить
                </Button>
              </div>

              {loadingDiscovered ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                </div>
              ) : discoveredTokens.length === 0 ? (
                <div className="text-center py-8 text-purple-300">
                  <Radar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Нет обнаруженных токенов</p>
                  <p className="text-sm opacity-75">Нажмите "Сканировать" для поиска</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {discoveredTokens.map((token) => (
                    <div 
                      key={token.master_address}
                      className="bg-white/5 rounded-lg p-3 border border-white/10 hover:border-purple-500/50 transition-all flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {token.symbol?.charAt(0) || '?'}
                        </div>
                        <div>
                          <div className="text-white font-medium flex items-center gap-2">
                            {token.symbol || 'UNKNOWN'}
                            {token.trading_enabled && (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                                Active
                              </Badge>
                            )}
                          </div>
                          <div className="text-purple-300 text-xs">{token.name || 'Unknown'}</div>
                          <div className="text-purple-400/70 text-xs flex items-center gap-1 mt-0.5">
                            <Clock className="h-3 w-3" />
                            {token.created_at ? formatDate(token.created_at) : 'Unknown'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-white font-medium">
                            {formatPrice(token.last_price_usd)}
                          </div>
                          <div className="text-purple-300 text-xs flex items-center gap-1">
                            <Droplets className="h-3 w-3" />
                            {formatNumber(token.ton_liq_collected)} TON
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`https://tonviewer.com/${token.master_address}`, '_blank')}
                            className="bg-white/5 border-white/20 text-purple-200 hover:bg-white/10"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => addToWatchlist(token)}
                            className="bg-purple-500 hover:bg-purple-600 text-white"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Watch
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Separate component for token info display
const TokenInfoCard = ({ tokenInfo, tonPrice, onAdd, adding }) => {
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '—';
    if (num >= 1e9) return (num / 1e9).toFixed(decimals) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(decimals) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(decimals) + 'K';
    return num.toFixed(decimals);
  };

  const formatPrice = (price) => {
    if (!price) return '$0';
    if (price < 0.00001) return `$${price.toExponential(2)}`;
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className="bg-white/10 backdrop-blur-sm border-white/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
              {tokenInfo.symbol?.charAt(0) || '?'}
            </div>
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                {tokenInfo.name || 'Unknown Token'}
                <Badge variant="outline" className="text-purple-300 border-purple-400">
                  {tokenInfo.symbol || 'UNKNOWN'}
                </Badge>
              </CardTitle>
              <CardDescription className="text-purple-200 font-mono text-xs">
                {tokenInfo.token_address?.slice(0, 20)}...
                <a 
                  href={`https://tonviewer.com/${tokenInfo.token_address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 inline-flex items-center text-purple-400 hover:text-purple-300"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              </CardDescription>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {tokenInfo.trading_enabled ? (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Trading Active
              </Badge>
            ) : (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                <XCircle className="h-3 w-3 mr-1" />
                Trading Disabled
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Price Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <div className="flex items-center gap-2 text-purple-300 text-sm mb-1">
              <Coins className="h-4 w-4" />
              Цена
            </div>
            <div className="text-xl font-bold text-white">
              {formatPrice(tokenInfo.price_usd)}
            </div>
            <div className="text-xs text-purple-300">
              {tokenInfo.price_ton?.toFixed(9)} TON
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <div className="flex items-center gap-2 text-purple-300 text-sm mb-1">
              <TrendingUp className="h-4 w-4" />
              Total Supply
            </div>
            <div className="text-xl font-bold text-white">
              {formatNumber(tokenInfo.total_supply)}
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <div className="flex items-center gap-2 text-purple-300 text-sm mb-1">
              <Droplets className="h-4 w-4" />
              Ликвидность
            </div>
            <div className="text-xl font-bold text-white">
              {formatNumber(tokenInfo.ton_liq_collected)} TON
            </div>
            <div className="text-xs text-purple-300">
              ${formatNumber(tokenInfo.ton_liq_collected * tonPrice)}
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <div className="flex items-center gap-2 text-purple-300 text-sm mb-1">
              <Users className="h-4 w-4" />
              Holders
            </div>
            <div className="text-xl font-bold text-white">
              {tokenInfo.holders_count || '—'}
            </div>
          </div>
        </div>

        {/* BCL Data Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
            <div className="text-purple-300 text-xs mb-1">BCL Supply</div>
            <div className="text-white font-medium">
              {formatNumber(tokenInfo.bcl_supply)}
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
            <div className="text-purple-300 text-xs mb-1">Liq Supply</div>
            <div className="text-white font-medium">
              {formatNumber(tokenInfo.liq_supply)}
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
            <div className="flex items-center gap-1 text-purple-300 text-xs mb-1">
              <Percent className="h-3 w-3" />
              Trade Fee
            </div>
            <div className="text-white font-medium">
              {tokenInfo.trade_fee_percent?.toFixed(2)}%
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
            <div className="flex items-center gap-1 text-purple-300 text-xs mb-1">
              <Clock className="h-3 w-3" />
              Last Trade
            </div>
            <div className="text-white font-medium text-sm">
              {formatDate(tokenInfo.last_trade_datetime)}
            </div>
          </div>
        </div>

        {/* Author Info */}
        {tokenInfo.author && (
          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
            <div className="text-purple-300 text-xs mb-1">Author</div>
            <div className="text-white font-mono text-sm">
              {tokenInfo.author}
              <a 
                href={`https://tonviewer.com/${tokenInfo.author}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 inline-flex items-center text-purple-400 hover:text-purple-300"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button 
            onClick={onAdd}
            disabled={adding}
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
          >
            {adding ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Добавить в Watchlist
          </Button>

          <Button 
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            onClick={() => {
              navigator.clipboard.writeText(tokenInfo.token_address);
              toast.success('Адрес скопирован');
            }}
          >
            Copy Address
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BlumTokenScanner;
